<template>
  <div>
    <div class="mt-5 text-center">
      <HMFilePreview
        class="d-inline-block"
        v-if="media.media"
        :file="media.media"
        :checkIfVideoInProgress="true"
        :videoProcessingText="$t('GENERAL.TEXT.VIDEO_WILL_FINISH_PROCESSING')"
      />
    </div>
    <h1 class="mt-4">
      {{ media.title }}
    </h1>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center pb-3"
      :class="{
        'border-bottom':
          (media.options && media.options.length) ||
          media.description ||
          media.description_new,
      }"
    >
      <div class="d-flex mt-2 align-items-center">
        <HMTag
          :title="$t(getMediaTypeLabel(media.media.file_content))"
          class="bg-charcoal c-white mr-3"
        />
        <HMInstructorsStack
          v-if="media.instructor"
          :instructors="[media.instructor]"
          size="32px"
        />
      </div>
      <div class=""></div>
    </div>
    <div
      v-if="media.options && media.options.length"
      class="d-flex align-items-center py-2 border-bottom"
    >
      <HMTag
        class="smallest mr-1 my-1"
        :title="tag.option_name"
        v-for="tag in media.options"
        :key="tag.id"
      />
    </div>
    <div class="py-3 border-bottom">
      <h4>{{ $t('MEDIA_LIBRARY.LABEL.MORE_INFORMATION') }}</h4>
      <div class="row">
        <template v-for="(infoItem, index) in info">
          <div
            v-if="infoItem.text"
            class="col-4 col-md-2 my-1"
            :key="'tlabel' + index"
          >
            {{ infoItem.label }}
          </div>
          <div
            v-if="infoItem.text"
            class="col-8 col-md-4 c-charcoal my-1"
            :key="'tvalue' + index"
          >
            <a v-if="infoItem.href" :href="infoItem.href" target="_blank">
              {{ infoItem.text }}
            </a>
            <div v-if="!infoItem.href">
              {{ infoItem.text }}
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="includedAnywhere" class="py-3 border-bottom">
      <h4 class="pt-3">{{ $t('MEDIA_LIBRARY.LABEL.INCLUDED_IN') }}</h4>
      <div v-if="includedInProducts.length">
        <h6>{{ $t('MEDIA_LIBRARY.LABEL.INCLUDED_IN_PRODUCTS') }}</h6>
        <div class="d-flex flex-row flex-wrap justify-content-start">
          <router-link
            v-for="product in includedInProducts"
            :key="product.id"
            :to="{
              name: 'product-edit-content',
              params: { id: product.id },
            }"
          >
            <HMTag class="mr-3">
              {{ product.product_name }}
            </HMTag>
          </router-link>
        </div>
      </div>
      <div v-if="includedInLessons.length">
        <h6>{{ $t('MEDIA_LIBRARY.LABEL.INCLUDED_IN_LESSONS') }}</h6>
        <div class="d-flex flex-row flex-wrap justify-content-start">
          <router-link
            v-for="lesson in includedInLessons"
            :key="lesson.id"
            :to="{
              name: 'product-edit-lessons',
              params: { id: lesson.product_id },
            }"
          >
            <HMTag class="mr-3">
              {{ lesson.lesson_name }} | {{ lesson.product__product_name }}
            </HMTag>
          </router-link>
        </div>
      </div>
      <div v-if="includedInPlaylists.length">
        <h6>{{ $t('MEDIA_LIBRARY.LABEL.INCLUDED_IN_PLAYLISTS') }}</h6>
        <div class="d-flex flex-row flex-wrap justify-content-start">
          <router-link
            v-for="playlist in includedInPlaylists"
            :key="playlist.id"
            :to="{
              name: 'product-edit-content',
              params: { id: playlist.product_id },
            }"
          >
            <HMTag class="mr-3">
              {{ playlist.playlist_name }} |
              {{ playlist.product__product_name }}
            </HMTag>
          </router-link>
        </div>
      </div>
    </div>
    <div v-if="media.description || media.description_new">
      <h4 class="pt-3">{{ $t('MEDIA_LIBRARY.LABEL.DESCRIPTION') }}</h4>
      <HMCustomContent
        class="py-3"
        :html="media.description"
        :json="media.description_new"
      />
    </div>
    <div>
      <h3 class="mt-5 mb-4">
        {{ $t('MEDIA_LIBRARY.TITLE.ATTACHMENTS') }}
      </h3>
      <HMSimpleActionCard
        class="mb-3"
        :prefixIcon="['fas', 'image']"
        v-for="file in media.attachments"
        :key="file.id"
        :title="cleanedFileName(file.file_name)"
        @click="downloadAttachment(file)"
      >
      </HMSimpleActionCard>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { getMediaTypeLabel } from '@/core/utils/media';
import HMTag from '@/web/components/common/HMTag';
import HMInstructorsStack from '@/web/components/common/HMInstructorsStack.vue';
import HMCustomContent from '@/web/components/common/HMCustomContent';
import { formatVideoLengthFromSeconds } from '@/web/utils/media';
import { cleanedFileName } from '@/core/utils/utils';

export default Vue.extend({
  name: 'LibraryMediaDetails',
  components: {
    HMCustomContent,
    HMTag,
    HMInstructorsStack,
  },
  props: {
    media: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {};
  },
  computed: {
    info() {
      const media = this.media.media;
      if (!media) return [];
      const result = this.fileToInfo(media);
      result.push({
        label: this.$t('MEDIA_LIBRARY.LABEL.VIEWS'),
        text: this.media.views,
      });
      result.push({
        label: `${this.$t('MEDIA_LIBRARY.LABEL.RELEASE_ON')}:`,
        text: this.$formatDate(this.media.release_on),
      });
      return result;
    },
    includedInProducts() {
      return this.media.included_in_products || [];
    },
    includedInLessons() {
      return this.media.included_in_lessons || [];
    },
    includedInPlaylists() {
      return this.media.included_in_playlists || [];
    },
    includedAnywhere() {
      return (
        (this.includedInProducts?.length ||
          0 + this.includedInLessons?.length ||
          0 + this.includedInPlaylists?.length ||
          0) > 0
      );
    },
  },
  methods: {
    getMediaTypeLabel,
    cleanedFileName,
    fileToInfo(file) {
      return [
        {
          label: this.$t('MEDIA_LIBRARY.LABEL.FILE_NAME'),
          text: file.file_name,
        },
        {
          label: this.$t('MEDIA_LIBRARY.LABEL.FILE_TYPE'),
          text: file.file_type,
        },
        file.width &&
          file.height && {
            label: this.$t('MEDIA_LIBRARY.LABEL.RESOLUTION'),
            text: `${file.width}x${file.height}`,
          },
        {
          label: this.$t('MEDIA_LIBRARY.LABEL.FILE_SIZE'),
          text: file.file_size
            ? (file.file_size / 1048576).toFixed(2) + ' MB'
            : null,
        },
        file.video_length && {
          label: this.$t('MEDIA_LIBRARY.LABEL.VIDEO_LENGTH'),
          text: formatVideoLengthFromSeconds(file.video_length),
        },
        {
          label: this.$t('MEDIA_LIBRARY.LABEL.UPLOADER'),
          text: file.owner.full_name,
        },
        {
          label: this.$t('MEDIA_LIBRARY.LABEL.UPLOADED'),
          text:
            this.$formatDate(file.uploaded) +
            `${
              file.file_type === 'vimeo' && !file.is_uploaded
                ? ' ' + this.$t('MEDIA_LIBRARY.LABEL.EMBEDDED')
                : ''
            }`,
        },
        file.file_content == 'application' && {
          label: this.$t('MEDIA_LIBRARY.LABEL.LINK'),
          text: file.file_name,
          href: file.storage_url,
        },
      ].filter(item => item);
    },
    downloadAttachment(file) {
      return window.open(file.storage_url, '_blank');
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.info-table td {
  padding: 0;
}
</style>
